/**
 * What this device can encode and decode in hardware.
 *
 * Recovered from commit d58f00ed^, where it was deleted as collateral damage in
 * a UI revert. Its absence is why `video_conference/config/default.js` still
 * says "Clients that run the hardware probe pick the best codec explicitly" —
 * a comment describing code that had already been removed.
 *
 * IT DOES NOT CHANGE WHAT WE PUBLISH. It reports; useLocalMedia decides.
 *
 * What it decides changed on 2026-09-04: the camera now prefers H264, falling
 * back to VP8 only where the device cannot send H264 — Constrained Baseline
 * (42e01f) by preference, matched on profile-level-id rather than list
 * position. Previously VP8 was forced
 * whenever simulcast was on. The reason for the change is downstream, not in
 * the browser — H264 is the only codec the mixer's GPU can decode on NVDEC and
 * encode on NVENC, and VP8 cannot be hardware-encoded on any NVIDIA part.
 *
 * The old caution still stands and is now the risk we are carrying: H264
 * simulcast support in browsers is patchy and the failure mode is not "slightly
 * worse quality", it is a peer whose video never appears at all. This probe is
 * how that gets measured across the real device fleet, and `publishing` in the
 * `camera encoding ladder` log line is what to correlate it against.
 * See VIDEO_TRANSMISSION_ANALYSIS.md §8.1.
 *
 * Read `hwEncode` with suspicion. `hardwareAcceleration: 'prefer-hardware'` is
 * a PREFERENCE in the WebCodecs spec, not a requirement, so a `supported: true`
 * does not prove a hardware encoder exists — it proves the browser did not
 * object. The `!== 'prefer-software'` test below is the best signal available
 * and it is still only a signal.
 */

export type CodecName = 'h264' | 'vp9' | 'vp8' | 'av1'

export interface CodecCapability {
  codec: CodecName
  /** Matches mediasoup mimeType (e.g. 'video/H264') */
  mimeType: string
  hwEncode: boolean
  hwDecode: boolean
  /** false = codec not usable in this browser at all */
  swSupported: boolean
}

export type RankedCodecs = CodecCapability[]

/**
 * The mime strings, spelled once.
 *
 * They had already drifted: this file said `video/AV1` while the router config
 * negotiates its own list, and useLocalMedia carried a bare 'video/VP8'
 * literal. A probe reporting on a codec name the negotiation never uses is
 * worse than no probe.
 */
export const VIDEO_MIME = {
  h264: 'video/H264',
  vp9: 'video/VP9',
  vp8: 'video/VP8',
  av1: 'video/AV1',
} as const satisfies Record<CodecName, string>

// ─── WebCodecs probe configs ──────────────────────────────────────────────────

interface ProbeEntry {
  name: CodecName
  mimeType: string
  /** codec string for VideoEncoder */
  encCodec: string
  /** codec string for VideoDecoder */
  decCodec: string
}

const PROBE_ENTRIES: ProbeEntry[] = [
  {
    name: 'h264',
    mimeType: 'video/H264',
    // Constrained Baseline Level 3.1 — matches SFU profile-level-id '42e01f'.
    // Level 3.1 is required for 720p30; probing with the actual negotiated level
    // ensures old hardware that only handles Level 3.0 falls back to VP9/VP8.
    encCodec: 'avc1.42e01f',
    decCodec: 'avc1.42e01f',
  },
  {
    name: 'vp9',
    mimeType: 'video/VP9',
    encCodec: 'vp09.00.10.08', // VP9 Profile 0, Level 1.0, 8-bit
    decCodec: 'vp09.00.10.08',
  },
  {
    name: 'av1',
    mimeType: 'video/AV1',
    encCodec: 'av01.0.08M.08', // AV1 Main profile, Level 4.0, 8-bit
    decCodec: 'av01.0.08M.08',
  },
  {
    name: 'vp8',
    mimeType: 'video/VP8',
    encCodec: 'vp8',
    decCodec: 'vp8',
  },
]

const BASE_CONFIG = { width: 1280, height: 720, bitrate: 1_500_000, framerate: 30 }

// ─── Core probe ───────────────────────────────────────────────────────────────

async function probeOneCodec(entry: ProbeEntry): Promise<CodecCapability> {
  let hwEncode = false
  let hwDecode = false
  let swSupported = false

  // ── Encode side ──────────────────────────────────────────────────────────
  if (typeof VideoEncoder !== 'undefined') {
    try {
      // Prefer-hardware: returned config tells us what will actually be used
      const hwResult = await VideoEncoder.isConfigSupported({
        ...BASE_CONFIG,
        codec: entry.encCodec,
        hardwareAcceleration: 'prefer-hardware',
      })
      if (hwResult.supported) {
        // If the returned acceleration is NOT forced to prefer-software, hw is available
        hwEncode = hwResult.config?.hardwareAcceleration !== 'prefer-software'
        swSupported = true // if hw supported, sw is also supported
      }

      // Confirm software fallback independently
      if (!swSupported) {
        const swResult = await VideoEncoder.isConfigSupported({
          ...BASE_CONFIG,
          codec: entry.encCodec,
          hardwareAcceleration: 'prefer-software',
        })
        swSupported = !!swResult.supported
      }
    } catch {
      // Codec string not recognised by this browser — leave false
    }
  }

  // ── Decode side ──────────────────────────────────────────────────────────
  if (typeof VideoDecoder !== 'undefined') {
    try {
      const hwResult = await VideoDecoder.isConfigSupported({
        codec: entry.decCodec,
        hardwareAcceleration: 'prefer-hardware',
      } as VideoDecoderConfig)
      if (hwResult.supported) {
        hwDecode = hwResult.config?.hardwareAcceleration !== 'prefer-software'
      }
    } catch {
      // ignore
    }
  }

  return { codec: entry.name, mimeType: entry.mimeType, hwEncode, hwDecode, swSupported }
}

// ─── RTCRtpSender fallback ────────────────────────────────────────────────────
// When VideoEncoder is not available we check which codecs the browser's WebRTC
// stack supports, then apply a conservative hardware heuristic:
//   H264 → assume hardware on non-Firefox (all Chromium/Safari have VideoToolbox/QuickSync)
//   VP9  → assume hardware on Chromium (most have it for decode; encode is rarer)
//   VP8  → software-only
//   AV1  → software-only unless we can confirm otherwise

function rtcCapabilityFallback(): RankedCodecs {
  // Bare `RTCRtpSender` / `navigator` here threw a ReferenceError the moment
  // anything imported this module outside a browser — which is every unit test.
  if (typeof RTCRtpSender === 'undefined' || !RTCRtpSender.getCapabilities) return []
  if (typeof navigator === 'undefined') return []

  const rtcCaps = RTCRtpSender.getCapabilities('video')
  if (!rtcCaps) return []

  const supportedMimes = new Set(
    rtcCaps.codecs.map(c => c.mimeType.toLowerCase())
  )

  const isChromium = /chrome|chromium|edg/i.test(navigator.userAgent)
  const isSafari = /safari/i.test(navigator.userAgent) && !isChromium

  return PROBE_ENTRIES
    .filter(e => supportedMimes.has(e.mimeType.toLowerCase()))
    .map(e => ({
      codec: e.name,
      mimeType: e.mimeType,
      swSupported: true,
      hwEncode: (e.name === 'h264' && (isChromium || isSafari)) ||
                (e.name === 'vp9' && isChromium),
      hwDecode: (e.name === 'h264') ||
                (e.name === 'vp9' && isChromium) ||
                (e.name === 'av1' && isChromium),
    }))
}

// ─── Ranking ──────────────────────────────────────────────────────────────────
// Priority:
//   1. hw encode + hw decode
//   2. hw encode only
//   3. h264 sw (best compat/interop)
//   4. vp9 sw (better compression than vp8)
//   5. vp8 sw (universal baseline)
//   6. av1 sw (last resort — CPU heavy)

function rank(caps: CodecCapability[]): RankedCodecs {
  return [...caps]
    .filter(c => c.swSupported)
    .sort((a, b) => score(b) - score(a))
}

function score(c: CodecCapability): number {
  let s = 0
  if (c.hwEncode) s += 100
  if (c.hwDecode) s += 50
  // Tie-break by codec preference
  const order: Record<CodecName, number> = { h264: 40, vp9: 30, vp8: 20, av1: 10 }
  s += order[c.codec] ?? 0
  // Software AV1 is not a viable SEND choice at conference resolutions, and the
  // penalty has to exceed anything it can earn elsewhere — the original was -25,
  // which lost to the +50 for hardware DECODE, so on Chromium (hw AV1 decode,
  // software encode) AV1 ranked above VP8 for sending. Decode capability must
  // not rescue a codec we would have to encode in software.
  if (c.codec === 'av1' && !c.hwEncode) s -= 100
  return s
}

// ─── Singleton ───────────────────────────────────────────────────────────────

let _cache: Promise<RankedCodecs> | null = null

export function probeCodecCapabilities(): Promise<RankedCodecs> {
  if (_cache) return _cache

  _cache = (async (): Promise<RankedCodecs> => {
    // WebCodecs path
    if (typeof VideoEncoder !== 'undefined') {
      const results = await Promise.all(PROBE_ENTRIES.map(probeOneCodec))
      const ranked = rank(results)
      _snapshot = ranked
      return ranked
    }

    // RTCRtpSender fallback
    const fallback = rtcCapabilityFallback()
    const ranked = rank(fallback)
    _snapshot = ranked
    return ranked
  })()

  return _cache
}

/** Convenience: best codec name to use for sending, or 'vp8' if probe failed */
export async function preferredSendCodecName(): Promise<CodecName> {
  const ranked = await probeCodecCapabilities()
  return ranked[0]?.codec ?? 'vp8'
}


/**
 * What the probe found, if it has finished. Never blocks and never starts it.
 *
 * Exists so a log line can carry the probe's answer alongside the codec we
 * actually chose, which is the comparison that would justify changing the
 * choice later.
 */
let _snapshot: RankedCodecs | null = null
export function codecProbeSnapshot(): RankedCodecs | null {
  return _snapshot
}

/** Test seam. */
export function __resetProbe(): void { _cache = null; _snapshot = null }
